/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  SignUp, Room, Customer, Portfolio,
} from './types';
import store from './store';

export class Api {
  baseUrl: string;

  constructor() {
    this.baseUrl = store.state.apiBaseUrl
  }

  async completeSignup(id: string) {
    return this.postNoResponse(
      `${this.baseUrl}/signups/${id}/complete`,
      null,
      'Could not complete signup',
    )
  }

  async createPortfolio(body: Portfolio) {
    return this.post(`${this.baseUrl}/portfolios`, body, 'Could not save portfolio')
  }

  async createSignup(body: SignUp) {
    return this.post(
      `${this.baseUrl}/signups`,
      body,
      'Could not save signup',
    )
  }

  async deleteSignupImage(signupId: string, filename: string) {
    return this.delete(
      `${this.baseUrl}/signups/${signupId}/images/${filename}`,
      'Could not remove image',
    )
  }

  async deletePortfolioImage(portfolioId: string, filename: string) {
    return this.delete(
      `${this.baseUrl}/portfolios/${portfolioId}/photos/${filename}`,
      'Could not remove image',
    )
  }

  async getPortfolio(id: string) {
    return this.get(
      `${this.baseUrl}/portfolios/${id}`,
      'Could not fetch portfolio',
    )
  }

  async getPortfolios(skip = 0, take = 25) {
    return this.get(
      `${this.baseUrl}/portfolios?skip=${skip}&take=${take}`,
      'Could not fetch portfolios',
    )
  }

  async getRandomPortfolio() {
    return this.get(
      `${this.baseUrl}/portfolios/-/random`,
      'Could not fetch portfolio',
    )
  }

  async getSignup(id: string) {
    return this.get(
      `${this.baseUrl}/signups/${id}`,
      'Could not fetch signup',
    )
  }

  async getSignupByCode(code: string) {
    return this.get(
      `${this.baseUrl}/signup_codes/${code}`,
      'Could not fetch signup',
    )
  }

  async getSignups(skip = 0, take = 25) {
    return this.get(
      `${this.baseUrl}/signups?skip=${skip}&take=${take}&complete=true`,
      'Could not fetch signups',
    )
  }

  async getSignupImage(
    signupId: string, filename: string,
  ): Promise<Blob | null> {
    return this.fetchBinary(
      `${this.baseUrl}/signups/${signupId}/images/${filename}`,
      'Could not fetch image',
    )
  }

  async getSignupAdminImage(
    signupId: string, filename: string,
  ): Promise<Blob | null> {
    return this.fetchBinary(
      `${this.baseUrl}/signups/${signupId}/admin_images/${filename}`,
      'Could not fetch image',
    )
  }

  async putCustomer(id: string, body: Customer) {
    return this.put(
      `${this.baseUrl}/signups/${id}/customers`,
      body,
      'Could not update customer',
    )
  }

  async putDimensionsFiles(id: string, body: FormData) {
    const headers: any = {}
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }

    const response = await fetch(
      `${this.baseUrl}/signups/${id}/dimensions`,
      {
        method: 'PUT',
        body,
        headers,
      },
    )

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.message)
    }

    return response.json()
  }

  async putInspirationFiles(id: string, body: FormData) {
    const headers: any = {}
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }

    const response = await fetch(
      `${this.baseUrl}/signups/${id}/inspiration_photos`,
      {
        method: 'PUT',
        body,
        headers,
      },
    )

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.message)
    }

    return response.json()
  }

  async putRoom(id: string, body: Room) {
    return this.put(
      `${this.baseUrl}/signups/${id}/rooms`,
      body,
      'Could not save room',
    )
  }

  async putRoomFiles(id: string, body: FormData) {
    const headers: any = {}
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }

    const response = await fetch(
      `${this.baseUrl}/signups/${id}/room_photos`,
      {
        method: 'PUT',
        body,
        headers,
      },
    )

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.message)
    }

    return response.json()
  }

  async putPortfolioFiles(id: string, body: FormData) {
    const headers: any = {}
    if (store.state.adminToken) {
      headers['X-AUTH-KEY'] = store.state.adminToken
    }

    const response = await fetch(
      `${this.baseUrl}/portfolios/${id}/photos`,
      {
        method: 'PUT',
        body,
        headers,
      },
    )

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.message)
    }

    return response.json()
  }

  async sendContactMsg(name: string, email: string, message: string) {
    return this.postNoResponse(
      `${this.baseUrl}/contacts`,
      { name, email, message },
      'Could not send message',
    )
  }

  async updatePortfolio(id: string, body: FormData) {
    const headers: any = {}
    if (store.state.adminToken) {
      headers['X-AUTH-KEY'] = store.state.adminToken
    }

    const response = await fetch(
      `${this.baseUrl}/portfolios/${id}`,
      {
        method: 'PUT',
        body,
        headers,
      },
    )

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.message)
    }

    return response.json()
  }

  private async delete(url: string, failureMessage: string) {
    return this.fetchNoResponse(
      url,
      'DELETE',
      null,
      failureMessage,
    )
  }

  private async get(url: string, failureMessage: string) {
    return this.fetch(
      url,
      'GET',
      null,
      failureMessage,
    )
  }

  private async post(url: string, body: any, failureMessage: string) {
    return this.fetch(
      url,
      'POST',
      body,
      failureMessage,
    )
  }

  private async postNoResponse(url: string, body: any, failureMessage: string) {
    return this.fetchNoResponse(
      url,
      'POST',
      body,
      failureMessage,
    )
  }

  private async put(url: string, body: any, failureMessage: string) {
    return this.fetch(
      url,
      'PUT',
      body,
      failureMessage,
    )
  }

  private async fetch(
    url: string, method: string, body: any, failureMessage = 'Could not contact server',
  ) {
    const headers: any = {
      'Content-Type': 'application/json',
    }
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }
    if (store.state.adminToken) {
      headers['X-AUTH-KEY'] = store.state.adminToken
    }

    const response = await fetch(url, {
      method,
      body: body ? JSON.stringify(body) : null,
      headers,
    })

    if (!response.ok) {
      throw new Error(failureMessage)
    }

    return response.json()
  }

  private async fetchBinary(
    url: string, failureMessage = 'Could not contact server',
  ) {
    const headers: any = {}
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }
    if (store.state.adminToken) {
      headers['X-AUTH-KEY'] = store.state.adminToken
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(failureMessage)
    }

    return response.blob()
  }

  private async fetchNoResponse(
    url: string, method: string, body: any, failureMessage = 'Could not contact server',
  ) {
    const headers: any = {
      'Content-Type': 'application/json',
    }
    if (store.state.signupCode) {
      headers['X-SIGNUP-CODE'] = store.state.signupCode
    }
    if (store.state.adminToken) {
      headers['X-AUTH-KEY'] = store.state.adminToken
    }

    const response = await fetch(url, {
      method,
      body: body ? JSON.stringify(body) : null,
      headers,
    })

    if (!response.ok) {
      throw new Error(failureMessage)
    }
  }
}

export default new Api()
